import type { SmartCreationDraft } from "@adpilot/shared";
import { IconPhoto } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  ProductAdvertisingStatusBadge,
  ProductThumbnail,
} from "@/components/shared/product-thumbnail";
import { Avatar, AvatarFallback, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/preview-card";
import { useProductPreviewStatuses } from "@/hooks/use-product-preview-statuses";
import { useIsVendorShop } from "@/hooks/use-shop-account-type";
import { cn } from "@/lib/utils";

interface SelectedProductsPreviewProps {
  products: SmartCreationDraft["products"];
  shopId: string;
}

export function SelectedProductsPreview(props: SelectedProductsPreviewProps) {
  return props.products.length > 0 ? <ProductsPreview {...props} /> : null;
}

const VISIBLE_PRODUCTS = 4;

function ProductsPreview({ products, shopId }: SelectedProductsPreviewProps) {
  const { t } = useTranslation("smartCreation");
  const visibleProducts = products.slice(0, VISIBLE_PRODUCTS);
  const statuses = useProductPreviewStatuses(shopId, visibleProducts);
  const vendor = useIsVendorShop(shopId);
  const identityLabel = t(vendor ? "strategy.context.asins" : "strategy.context.skus");
  return (
    <fieldset
      aria-label={`${identityLabel} · ${products.length}`}
      className="group/avatar-group group/products m-0 flex min-w-0 shrink-0 items-center border-0 p-0"
    >
      {visibleProducts.map((product, index) => (
        <div
          key={product.sku}
          className={cn(
            "rounded-md transition-[margin] duration-200 ease-out",
            index > 0 && "-ml-4 group-hover/products:ml-1 group-focus-within/products:ml-1",
          )}
        >
          <ProductThumbnail
            imageUrl={product.imageUrl}
            label={product.title ?? product.asin}
            asin={product.asin}
            sku={vendor ? undefined : product.sku}
            productStatus={statuses.get(product.sku)}
            thumbnail={
              <Avatar className="size-8 rounded-md bg-surface after:border-0">
                <AvatarImage
                  src={product.imageUrl ?? undefined}
                  alt={product.title ?? product.asin}
                  className="object-contain p-0.5"
                />
                <AvatarFallback className="rounded-md">
                  <IconPhoto className="size-4" aria-hidden />
                </AvatarFallback>
              </Avatar>
            }
          />
        </div>
      ))}
      {products.length > VISIBLE_PRODUCTS ? (
        <HoverCard>
          <HoverCardTrigger
            delay={300}
            closeDelay={80}
            render={
              <Button
                variant="ghost"
                size="sm"
                aria-label={`${identityLabel} · +${products.length - VISIBLE_PRODUCTS}`}
                className="ml-1 h-auto w-fit rounded-md border-0 p-0 hover:bg-transparent focus-visible:ring-0"
              />
            }
          >
            <AvatarGroupCount className="rounded-md text-[10px] ring-0">
              +{products.length - VISIBLE_PRODUCTS}
            </AvatarGroupCount>
          </HoverCardTrigger>
          <HoverCardContent
            align="start"
            side="top"
            className="w-96 max-w-[calc(100vw-2rem)] flex-col text-left [text-wrap:wrap]"
          >
            <p className="font-semibold">
              {t("objectives.products.selected", { count: products.length })}
            </p>
            <OverflowProducts products={products.slice(VISIBLE_PRODUCTS)} shopId={shopId} />
          </HoverCardContent>
        </HoverCard>
      ) : null}
    </fieldset>
  );
}

/** Mounted only while the hover card is open, so the overflow statuses resolve on demand. */
function OverflowProducts({ products, shopId }: SelectedProductsPreviewProps) {
  const statuses = useProductPreviewStatuses(shopId, products);
  const vendor = useIsVendorShop(shopId);
  return (
    <ul className="mt-3 max-h-72 space-y-3 overflow-auto">
      {products.map((product) => {
        const status = statuses.get(product.sku);
        return (
          <li key={product.sku} className="flex items-center gap-3">
            <ProductThumbnail
              imageUrl={product.imageUrl}
              label={product.title ?? product.asin}
              asin={product.asin}
              sku={vendor ? undefined : product.sku}
              productStatus={status}
              size="context"
            />
            <span className="min-w-0">
              <span className="block break-words text-sm">{product.title ?? product.asin}</span>
              <span className="block break-all text-xs text-muted-foreground">
                {vendor ? product.asin : `${product.sku} · ${product.asin}`}
              </span>
              {status ? <ProductAdvertisingStatusBadge status={status} /> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
